import React from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Card, Space, Switch, Tooltip } from 'antd'
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { useTranslation } from 'react-i18next'

interface PredictionAnalysisProps {
    historicalData: Array<{ date: string; count: number }>
    days?: number
    versionName?: string
}

interface PredictionChartPoint {
    date: string
    actualCount: number | null
    predictedCount: number | null
}

const MIN_HISTORY_DAYS = 7
const LOOKBACK_DAYS = 30
const TREND_WINDOW_DAYS = 7
const DAILY_TREND_LIMIT_RATIO = 0.15
const TREND_DAMPING_FACTOR = 0.72
const FLUCTUATION_LIMIT_RATIO = 0.35
const FLUCTUATION_DAMPING_FACTOR = 0.82

const sortByDate = (
    data: Array<{ date: string; count: number }>
): Array<{ date: string; count: number }> =>
    [...data].sort((left, right) => left.date.localeCompare(right.date))

const getMedian = (values: number[]): number => {
    if (values.length === 0) {
        return 0
    }

    const sortedValues = [...values].sort((left, right) => left - right)
    const middleIndex = Math.floor(sortedValues.length / 2)

    return sortedValues.length % 2 === 0
        ? (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2
        : sortedValues[middleIndex]
}

const capOutliers = (values: number[]): number[] => {
    const median = getMedian(values)
    if (median <= 0) {
        return values
    }

    const upperLimit = median * 3
    return values.map((value) => Math.min(value, upperLimit))
}

const calculateAverage = (values: number[]): number =>
    values.length > 0
        ? capOutliers(values).reduce((sum, value) => sum + value, 0) /
          values.length
        : 0

const calculateWeightedAverage = (values: number[]): number => {
    const cappedValues = capOutliers(values)
    let weightedSum = 0
    let weightSum = 0

    for (let index = 0; index < cappedValues.length; index++) {
        const weight = index + 1
        weightedSum += cappedValues[index] * weight
        weightSum += weight
    }

    return weightSum > 0 ? weightedSum / weightSum : 0
}

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max)

const calculateDailyTrend = (counts: number[], baseline: number): number => {
    const maxDailyTrend = baseline * DAILY_TREND_LIMIT_RATIO
    if (counts.length >= TREND_WINDOW_DAYS * 2) {
        const recentCounts = counts.slice(-TREND_WINDOW_DAYS)
        const previousCounts = counts.slice(
            -TREND_WINDOW_DAYS * 2,
            -TREND_WINDOW_DAYS
        )
        const rawDailyTrend =
            (calculateAverage(recentCounts) - calculateAverage(previousCounts)) /
            TREND_WINDOW_DAYS

        return clamp(rawDailyTrend, -maxDailyTrend, maxDailyTrend)
    }

    const splitIndex = Math.floor(counts.length / 2)
    const previousCounts = counts.slice(0, splitIndex)
    const recentCounts = counts.slice(splitIndex)
    const rawDailyTrend =
        (calculateAverage(recentCounts) - calculateAverage(previousCounts)) /
        Math.max(recentCounts.length, previousCounts.length, 1)

    return clamp(rawDailyTrend, -maxDailyTrend, maxDailyTrend)
}

const buildFluctuationPattern = (counts: number[], baseline: number): number[] => {
    if (counts.length < 2) {
        return [0]
    }

    const fluctuationLimit = Math.max(1, baseline * FLUCTUATION_LIMIT_RATIO)
    const cappedCounts = capOutliers(counts)
    return cappedCounts
        .slice(1)
        .map((count, index) =>
            clamp(count - cappedCounts[index], -fluctuationLimit, fluctuationLimit)
        )
}

const buildPredictedData = (
    historicalData: Array<{ date: string; count: number }>,
    days: number
): PredictionChartPoint[] => {
    if (historicalData.length < MIN_HISTORY_DAYS) {
        return []
    }

    const recentData = sortByDate(historicalData).slice(-LOOKBACK_DAYS)
    const counts = recentData.map((item) => item.count)
    const recentCounts = counts.slice(-TREND_WINDOW_DAYS)
    const baseline = calculateWeightedAverage(recentCounts)
    const dailyTrend = calculateDailyTrend(counts, baseline)
    const fluctuationPattern = buildFluctuationPattern(recentCounts, baseline)
    const lastDate = new Date(recentData[recentData.length - 1].date)

    let dampedTrendTotal = 0
    let predictionValue = baseline
    return Array.from({ length: days }, (_, index) => {
        const predictionDate = new Date(lastDate)
        predictionDate.setDate(predictionDate.getDate() + index + 1)
        dampedTrendTotal += dailyTrend * TREND_DAMPING_FACTOR ** index
        const fluctuation =
            fluctuationPattern[index % fluctuationPattern.length] *
            FLUCTUATION_DAMPING_FACTOR ** index
        predictionValue += dailyTrend * TREND_DAMPING_FACTOR ** index + fluctuation

        return {
            date: predictionDate.toISOString().split('T')[0],
            actualCount: null,
            predictedCount: Math.max(
                0,
                Math.round((baseline + dampedTrendTotal + predictionValue) / 2)
            ),
        }
    })
}

export const PredictionAnalysis: React.FC<PredictionAnalysisProps> = ({
    historicalData,
    days = 14,
    versionName,
}) => {
    const { t } = useTranslation('statistics')
    const [showPrediction, setShowPrediction] = React.useState<boolean>(true)

    const actualValueLabel = t('charts.prediction.actualValue')
    const predictedValueLabel = t('charts.prediction.predictedValue')

    const predictedData = React.useMemo(() => {
        return buildPredictedData(historicalData, days)
    }, [days, historicalData])

    const chartData = React.useMemo(() => {
        const historical: PredictionChartPoint[] = historicalData.map((item) => ({
            date: item.date,
            actualCount: item.count,
            predictedCount: null,
        }))

        if (showPrediction && predictedData.length > 0 && historical.length > 0) {
            historical[historical.length - 1] = {
                ...historical[historical.length - 1],
                predictedCount: historical[historical.length - 1].actualCount,
            }
        }

        return showPrediction ? [...historical, ...predictedData] : historical
    }, [historicalData, predictedData, showPrediction])

    const title = versionName
        ? `${t('charts.prediction.title')} (${versionName})`
        : t('charts.prediction.title')

    return (
        <Card
            title={title}
            extra={
                <Space>
                    <Tooltip title={t('charts.prediction.tooltip')}>
                        <InfoCircleOutlined />
                    </Tooltip>
                    <Switch
                        checkedChildren={t('charts.prediction.showPrediction')}
                        unCheckedChildren={t('charts.prediction.hidePrediction')}
                        checked={showPrediction}
                        onChange={setShowPrediction}
                    />
                </Space>
            }
        >
            <div style={{ height: 400 }}>
                <ResponsiveContainer>
                    <LineChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 30,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            label={{
                                value: t('charts.prediction.dateAxis'),
                                position: 'insideBottomRight',
                                offset: -10,
                            }}
                            scale="band"
                            tickCount={Math.min(chartData.length, 10)}
                        />
                        <YAxis
                            label={{
                                value: t('charts.prediction.countAxis'),
                                angle: -90,
                                position: 'insideLeft',
                            }}
                        />
                        <RechartsTooltip
                            filterNull
                            formatter={(value, name) => [
                                `${value} ${t('charts.prediction.countUnit')}`,
                                name === actualValueLabel
                                    ? t('charts.prediction.actualTooltipLabel')
                                    : t('charts.prediction.predictedTooltipLabel'),
                            ]}
                            labelFormatter={(label) =>
                                `${t('charts.prediction.dateLabel')}: ${label}`
                            }
                        />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="actualCount"
                            name={actualValueLabel}
                            stroke="#1979C9"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="predictedCount"
                            name={predictedValueLabel}
                            stroke="#D62A0D"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={{ r: 4 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </Card>
    )
}
